import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  claimTask, releaseTask,
  claimPlatformOrderTask, releasePlatformOrderTask,
} from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import FulfillPaymentRequestModal from './FulfillPaymentRequestModal';
import CopyTextButton from './CopyTextButton';
import RecordPaymentReceivedModal from './RecordPaymentReceivedModal';
import { renderTextWithAddressLabels } from './AddressLabel';
import TxHashLink from './TxHashLink';
import { formatDate, formatCurrency, resolveUploadUrl, customerBalanceSign } from '../utils/helpers';
import {
  taskFullyPrepared, prepareButtonLabel,
  platformPayeeText, platformBankingDetailsText,
  prRecordedName, prRecordedLabel, prHasSalesNote,
  prReceipts, prProgressText, prRecordButtonLabel,
  prReceiptProgress, prReceiptRecordText, prReceiptRecordSub, prRecordedReceiptLines,
  prIsGrouped, prGroupBadgeText, prGroupClaimLabel, prGroupHeldByOther,
  prGroupScopeLabel, releaseBlockedByPrepared, platformPayeeProofs,
  remainingToPay, releaseConfirmText, releaseIsPerPayment,
  expenseSlips, expenseSlipLabel, expenseSlipSourceText, expenseSlipChip, expenseSlipWho,
  taskPayeeAccess, formatTaskAmount, formatTaskMoney, displayTaskTitle,
  expensePayeeGate, expenseGateInputFromTask, EXPENSE_RELEASE_BLOCKED_TEXT, prTaskCopyLine, prReceiptCopyLine } from '../utils/paymentTask';
import { APPROVAL_TASK_TYPE } from '../utils/platformApproval';
import useTaskActions from '../hooks/useTaskActions';
import NoticeTaskActions from './NoticeTaskActions';
import './PendingTasksModal.css';
// Reuse the payment-request fulfil modal's stylesheet so the platform-order
// fulfil modal (the SAME shared <FulfillPaymentModal>) is visually identical.
import './UploadReceiptModal.css';
import AdsTypeChip from './AdsTypeChip';
import TaskProgressBar from './TaskProgressBar';
import { formatPlatformAccountLabel } from '../utils/paymentTask';

// ---- Pool categories (operator 2026-10-09: "most of the time finance doesn't
// need to know usdt has been credited etc, then it kinda block the way where
// the finance have to scroll through it to see task that really matters").
// The pool is one list; this only decides WHICH cards are rendered — it never
// re-sorts and never touches a card's actions. A task type missing from the
// map belongs to no category and shows under "All" only.
const POOL_CATEGORY_ALL = 'all';
const POOL_CATEGORIES = [
  { key: 'payouts',  label: 'Payouts',               empty: 'No payouts waiting.',            types: ['payment_request'] },
  { key: 'received', label: 'Payments received',     empty: 'No payments to record.',         types: ['payment_received'] },
  { key: 'platform', label: 'Platform orders',       empty: 'No platform orders waiting.',    types: ['platform_order', APPROVAL_TASK_TYPE] },
  { key: 'expenses', label: 'Expenses',              empty: 'No expenses waiting.',           types: ['expense_payment', 'expense_approval'] },
  // Notices are ONE task type (pr_notification) with two sources — split by the
  // row's source (operator 2026-10-09: "Crypto and bank (fiat) notice should
  // be separated"): an on-chain / wallet transfer carries blockchain_transaction_id
  // or crypto_transaction_id; a "PR from <payer>" bank credit carries
  // transaction_id. `types` is left empty for both — poolCategoryOf decides.
  { key: 'crypto',   label: 'Crypto notices',        empty: 'No crypto notices.',             types: [] },
  { key: 'bank',     label: 'Bank notices',          empty: 'No bank (fiat) notices.',        types: [] },
];
const POOL_CATEGORY_BY_TYPE = {};
POOL_CATEGORIES.forEach((c) => c.types.forEach((t) => { POOL_CATEGORY_BY_TYPE[t] = c.key; }));
const poolCategoryOf = (task) => {
  if (task.task_type === 'pr_notification') {
    return (task.blockchain_transaction_id || task.crypto_transaction_id) ? 'crypto' : 'bank';
  }
  return POOL_CATEGORY_BY_TYPE[task.task_type] || null;
};
// Which pill is selected when nothing is remembered for this user. Finance is
// deliberately 'all' (do not guess); switching finance to start on their
// payouts is the one-line change `finance: 'payouts'`.
const POOL_DEFAULT_CATEGORY_BY_ROLE = { finance: POOL_CATEGORY_ALL };
// ONE remembered pill per user, SHARED by the dashboard pop-up and Task Tracking's
// Unclaimed tab (the pop-up is a shortcut view of Task Tracking — operator
// 2026-10-09 — so the pill you pick follows you). The COUNTS are per surface:
// each list counts what it renders (the pop-up includes tasks that are already
// claimed, Unclaimed only what nobody holds), and says so under the pills. When
// the remembered pill has nothing on THIS list the list shows All and says why
// (audit D-22: it used to switch silently).
const poolCategoryStorageKey = (userId) => `pendingTasks.category.${userId}`;
const readPoolCategory = (userId, role) => {
  try {
    const v = localStorage.getItem(poolCategoryStorageKey(userId));
    if (v === POOL_CATEGORY_ALL || POOL_CATEGORIES.some((c) => c.key === v)) return v;
  } catch (e) { /* storage blocked / unavailable — fall through to the default */ }
  return POOL_DEFAULT_CATEGORY_BY_ROLE[role] || POOL_CATEGORY_ALL;
};
const writePoolCategory = (userId, value) => {
  try { localStorage.setItem(poolCategoryStorageKey(userId), value); } catch (e) { /* per-viewer convenience only */ }
};

// The rendering + actions for the pending-task pool, shared by the Pending Tasks
// modal (Dashboard) and the Task Tracking "Available" tab. Both feed it the same
// GET /api/pending-tasks data, so the two surfaces can never show different tasks
// or different actions. `onClose` is called after a handler navigates away; in
// the tab context there is no modal to close, so it defaults to a no-op.
// trackingLink: show the "Open in Task Tracking" jump on every card. The
// dashboard's Pending Tasks pop-up is a SHORTCUT VIEW of Task Tracking (same
// task records, same pool endpoint) — operator 2026-10-09: "there should
// always be a shortcut that we can click on pending task then goes directly
// to where the task is, at task tracking module". Task Tracking's own
// Available tab renders this list too and passes false (it is already there).
// focusTaskId: the card to highlight when Task Tracking was opened on a task
// that sits in the pool (the jump from the dashboard pop-up / a notification).
// unclaimedOnly: Task Tracking's "Unclaimed" tab — render only the tasks NOBODY
// holds yet (the ones you can claim). The pool itself also carries what you
// already hold (and, for an administrator, what everyone holds); those live under
// My Tasks / All Tasks. The category pills count the filtered list.
function PendingTaskList({ pendingTasks: poolTasks, onTaskUpdate, user, onClose = () => {}, trackingLink = true, focusTaskId = null, unclaimedOnly = false }) {
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [copiedTaskId, setCopiedTaskId] = useState(null);
  const [claimingTaskId, setClaimingTaskId] = useState(null);
  // The payment_received task whose finance record-PR modal is open.
  const [recordPrTask, setRecordPrTask] = useState(null);

  // Category strip (see POOL_CATEGORIES). The choice is remembered per user in
  // localStorage; a stored choice that has no tasks right now falls back to All
  // FOR DISPLAY only (the stored preference is kept). Once the user clicks a
  // pill in this view their pick is honoured as-is — emptying the category you
  // are working through shows "No payouts waiting" rather than yanking you to All.
  const userId = user?.id;
  const userRole = user?.role;
  // Shared with Task Tracking's cards: the approve/reject decision (decidingTaskId
  // is the one in flight, so both buttons disable while the request is out) and
  // the transaction deep-links. The pop-up closes itself after navigating away.
  const {
    decidingTaskId, handleApproveSubmission, handleRejectSubmission, handleTxAction, handleAssignToCustomer,
    claimingNoticeId, handleClaimNotice, handleCreateOrderFromNotice, handleCreatePlatformOrderFromNotice,
  } = useTaskActions({ refresh: onTaskUpdate, afterNavigate: onClose });
  // Unheld = no claimant (claimed_by is the server-derived claim) and no peer
  // holding its payment-request group (prGroupHeldByOther — null since the
  // one-payment-per-claim rule, kept so the two "held" pills and this filter
  // can never disagree).
  const pendingTasks = useMemo(
    () => (unclaimedOnly ? poolTasks.filter((t) => !t.claimed_by && !prGroupHeldByOther(t, userId)) : poolTasks),
    [poolTasks, unclaimedOnly, userId]
  );
  const [categoryChoice, setCategoryChoice] = useState(() => readPoolCategory(userId, userRole));
  const [categoryPicked, setCategoryPicked] = useState(false);
  useEffect(() => {
    setCategoryChoice(readPoolCategory(userId, userRole));
    setCategoryPicked(false);
  }, [userId, userRole]);
  const pickCategory = (key) => {
    setCategoryChoice(key);
    setCategoryPicked(true);
    writePoolCategory(userId, key);
  };
  const categoryCounts = useMemo(() => {
    const counts = {};
    pendingTasks.forEach((t) => {
      const k = poolCategoryOf(t);
      if (k) counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [pendingTasks]);
  let activeCategory = categoryChoice;
  // The remembered pill is empty on this list → show All, and say so.
  let emptyChoiceLabel = null;
  if (activeCategory !== POOL_CATEGORY_ALL && !categoryPicked && !categoryCounts[activeCategory]) {
    emptyChoiceLabel = POOL_CATEGORIES.find((c) => c.key === activeCategory)?.label || null;
    activeCategory = POOL_CATEGORY_ALL;
  }
  // A jump onto a specific card (dashboard pop-up / notification → Task
  // Tracking) must land on a VISIBLE card: when the focused task is outside the
  // remembered category, show All until the user picks a pill themselves.
  if (activeCategory !== POOL_CATEGORY_ALL && focusTaskId != null && !categoryPicked) {
    const focused = pendingTasks.find((t) => t.id === focusTaskId);
    if (focused && poolCategoryOf(focused) !== activeCategory) activeCategory = POOL_CATEGORY_ALL;
  }
  const visibleTasks = activeCategory === POOL_CATEGORY_ALL
    ? pendingTasks
    : pendingTasks.filter((t) => poolCategoryOf(t) === activeCategory);

  // A task's amount (and what is left of it): ONE formatter with Task Tracking
  // (utils/paymentTask formatTaskAmount / formatTaskMoney) — the token or
  // currency is read off the LIVE fields, never parsed out of the title, and a
  // coin keeps its precision (audit 2026-10 D-11). MYR-only figures on this
  // card use the shared formatCurrency from utils/helpers.
  const getTaskAmountDisplay = formatTaskAmount;

  // handleCreateOrderFromNotice / handleCreatePlatformOrderFromNotice /
  // handleClaimNotice are hooks/useTaskActions — shared with Task Tracking's
  // cards (a claimed notice is worked from My Tasks too).

  // handleAssignToCustomer / handleTxAction / the approve + reject handlers are
  // hooks/useTaskActions — shared with Task Tracking's cards.

  const handleUploadReceipt = (task) => {
    setSelectedTask(task);
    setShowUploadModal(true);
  };

  const handleReceiptUploaded = () => {
    if (onTaskUpdate) {
      onTaskUpdate();
    }
    setShowUploadModal(false);
    setSelectedTask(null);
  };

  const getAccountDetailsLines = (task) => {
    // expense_payment payee details live on their own expense_payee_* columns
    // (never bank_name/account_name — those are payment_request's), so this is
    // a genuinely separate source, branched first. Both handleCopyBankingDetails
    // and bankingDetailsText call through this one function, so branching here
    // is enough to make both "reuse" the expense payee text automatically.
    if (task.task_type === 'expense_payment') {
      const details = (task.expense_payee_details || '').toString().trim();
      if (details) return details.split('\n').map(l => l.trim()).filter(l => l);
      return [task.expense_payee_account_name || '', task.expense_payee_bank_name || '', task.expense_payee_account_number || '']
        .map(l => l.toString().trim())
        .filter(l => l);
    }
    const bankName = (task.bank_name || '').toString().trim();
    const accountName = (task.account_name || '').toString().trim();
    const hasMultiLine = bankName.includes('\n') || accountName.includes('\n');

    if (hasMultiLine) {
      const details = (bankName.includes('\n') ? bankName : accountName);
      return details.split('\n').map(l => l.trim()).filter(l => l);
    }

    return [accountName || task.transaction_account_name || '', bankName || task.transaction_bank_name || '', task.account_number || '']
      .map(l => l.toString().trim())
      .filter(l => l);
  };

  const handleCopyBankingDetails = (task) => {
    const lines = getAccountDetailsLines(task);
    // Remaining-to-pay, NOT the full requested amount (see bankingDetailsText).
    const amount = task.amount ? remainingToPay(task).toFixed(2) : '';
    const textToCopy = [...lines, amount].filter(l => l).join('\n');

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedTaskId(task.id);
      setTimeout(() => {
        setCopiedTaskId(null);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleClaimTask = async (task) => {
    setClaimingTaskId(task.id);
    try {
      await claimTask(task.id);
      showSuccess('Task claimed successfully! You can now copy the banking details.');
      // Refresh tasks to show updated claim status and reveal banking details
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (err) {
      console.error('Failed to claim task:', err);
      showError(err.response?.data?.error || 'Failed to claim task');
      // A failed claim almost always means our snapshot was stale — someone
      // else grabbed it (409). Refetch so the pool shows the real claim state
      // instead of still offering a Claim button (denorm Phase 6 / EX3).
      if (onTaskUpdate) onTaskUpdate();
    } finally {
      setClaimingTaskId(null);
    }
  };

  // Drop a claimed payment_request back to the pool. Only offered before any
  // tranche is prepared/paid; the backend rejects otherwise.
  const handleReleaseTask = async (task) => {
    // ONE wording (utils/paymentTask releaseConfirmText): a request payment says
    // only THIS payment goes back; a plain task says what a release keeps.
    if (!window.confirm(releaseConfirmText(task))) return;
    try {
      const res = await releaseTask(task.id);
      showSuccess(res?.message || 'Task released');
      if (onTaskUpdate) onTaskUpdate();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to release task');
      // Stale snapshot (task changed hands / gained a payment) → refetch (Phase 6).
      if (onTaskUpdate) onTaskUpdate();
    }
  };

  // ---- Platform-order task actions (the finance twin of the payment_request
  // claim/release/fulfil flow above). Claiming/releasing key on the ORDER id,
  // not the pending_tasks id — see server.js /platform-orders/:id/claim-task. ----

  // Claim the order task in place (same endpoint the /platform order-detail
  // "claim" button calls), so finance never has to leave the list to start work.
  const handleClaimPlatformTask = async (task) => {
    setClaimingTaskId(task.id);
    try {
      // A split buy's card IS one specific payment — claiming it names that
      // payment (the server refuses a seq-less claim on a split order so
      // nothing is ever auto-assigned; operator report 2026-08-28: the pool
      // Claim button 400'd on every split task). Single-task orders pass
      // null, byte-identical to before.
      await claimPlatformOrderTask(task.platform_order_id, task.platform_order_detail_seq != null ? task.platform_order_detail_seq : null);
      showSuccess('Order task claimed — you can now prepare payments.');
      if (onTaskUpdate) onTaskUpdate();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to claim order task');
      // Stale snapshot — another finance user already claimed it (409). Refetch
      // so the pool stops offering the Claim button (denorm Phase 6 / EX3).
      if (onTaskUpdate) onTaskUpdate();
    } finally {
      setClaimingTaskId(null);
    }
  };

  // Drop a claimed order task back to the pool. The backend refuses only while
  // an UNCONFIRMED (prepared) payment sits on the claim — completed payments
  // stay and the next claimer continues the balance (mid-flight hand-off).
  const handleReleasePlatformTask = async (task) => {
    if (!window.confirm(releaseConfirmText(task))) return;
    try {
      // Release is PER PAYMENT, exactly like claim above: a split buy's card IS
      // one specific payment, so it names it (the server releases only that
      // payment and refuses a seq-less release of a split order). Single-task
      // orders pass null — the server releases the one task you hold.
      const res = await releasePlatformOrderTask(task.platform_order_id, task.platform_order_detail_seq != null ? task.platform_order_detail_seq : null);
      showSuccess(res?.message || 'Order task released');
      if (onTaskUpdate) onTaskUpdate();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to release order task');
      // Stale snapshot (order changed hands / gained a payment) → refetch (Phase 6).
      if (onTaskUpdate) onTaskUpdate();
    }
  };

  // ---- ON-BEHALF SUBMISSION APPROVAL (task_type === APPROVAL_TASK_TYPE).
  // This task is BORN CLAIMED to the account's handler at insert time
  // (utils/platformApproval.js) — it is that one person's decision, never
  // pool work — so there is no claim/release/complete/dismiss step, only a
  // decision (handleApproveSubmission / handleRejectSubmission, from
  // hooks/useTaskActions). The server refuses those other verbs fail-closed
  // (403 APPROVAL_TASK_NOT_CLAIMABLE / 400 APPROVAL_TASK_NOT_RELEASABLE /
  // APPROVAL_TASK_DECISION_ONLY), so this card must never offer them. ----

  // ONE WRITER, ONE HOST (operator rule 2026-08-02). Preparing money on a
  // platform order is done in exactly one place — the fulfil modal hosted by
  // Platform Management — so there can never be two surfaces writing
  // platform_order_payments with different validation, gating or copy. The task
  // pool's job stops at "claim it and show me the shortcut card"; pressing
  // prepare hands over to the core module, using the SAME deep link the
  // notifications use (notificationRoute → selectedPlatformOrderId).
  const handleAddPlatformPayment = (task) => {
    navigate('/platform', {
      state: { selectedPlatformOrderId: task.platform_order_id, fromNotification: true },
    });
    onClose();
  };

  // ---- Platform-order payee helpers (platformPayeeText / platformBankingDetailsText
  // / platformPayeeEntries) live in utils/paymentTask so the card's banking panel
  // and the shared fulfil modal render the SAME payee — imported above. ----
  const handleCopyPlatformPayeeDetails = (task) => {
    navigator.clipboard.writeText(platformBankingDetailsText(task)).then(() => {
      setCopiedTaskId(task.id);
      setTimeout(() => setCopiedTaskId(null), 2000);
    }).catch((err) => {
      console.error('Failed to copy:', err);
    });
  };

  // The platform_order fulfil wiring lives in <FulfillPlatformOrderModal>, hosted
  // by Platform Management (the module that owns the order). This pool only
  // navigates there — see handleAddPlatformPayment.

  // Compute the customer-balance pill (if any) once, so the JSX below is tidy.
  const customerBalancePill = (task) => {
    const raw = task.customer_myr_balance;
    if (raw === undefined || raw === null) return null;
    // ONE balance wording (utils/helpers customerBalanceSign). The CSS tone
    // names are historical: --credit = the customer owes us, --debit = we hold
    // credit for them.
    const { sign, label } = customerBalanceSign(raw);
    const tone = { owes: 'credit', credit: 'debit', settled: 'neutral' }[sign];
    return (
      <span className={`balance-pill balance-pill--${tone}`}>
        {formatCurrency(Math.abs(parseFloat(raw) || 0))} · {label}
      </span>
    );
  };

  // Amount still LEFT to pay: remainingToPay (utils/paymentTask) is the ONE
  // definition — the header, the copy buttons and the payee block all read it.
  const isPartiallyAllocated = (task) =>
    (task.task_type === 'payment_request' || task.task_type === 'platform_order') &&
    parseFloat(task.paid_total || 0) > 0.005 &&
    remainingToPay(task) > 0;

  // Banking details stringified for display + copy. A single source of truth
  // so the copy clipboard and the on-screen block don't drift. On a PARTIALLY
  // allocated task the copied amount is the REMAINDER — copying the full
  // requested figure made finance pay the whole thing again.
  const bankingDetailsText = (task) =>
    [...getAccountDetailsLines(task), remainingToPay(task).toFixed(2)].join('\n');

  // platform_order and the on-behalf approval task (utils/platformApproval.js)
  // share their money-leg shape: same payee source (platform_payee_json), the
  // same order screenshot, the same banking panel. ONE check, so the panel's
  // gating and its payee-source branches can never drift between the two
  // task types.
  const isPlatformShaped = (t) => t.task_type === 'platform_order' || t.task_type === APPROVAL_TASK_TYPE;

  const renderActions = (task) => {
    // Common: claimed-by pill shows inline inside the actions row.
    const claimedPill = task.claimed_by ? (
      <span className={`claimed-pill ${task.claimed_by === user?.id ? 'claimed-pill--self' : ''}`}>
        {task.claimed_by === user?.id ? 'Claimed by you' : `Claimed by ${task.claimed_by_username}`}
      </span>
    ) : null;

    // CLAIM GROUP (2026-08-06). Two facts, both from the server: this card is
    // one payment of a set that is claimed together, and — when a peer already
    // holds the set — WHO. The pool hides their siblings from this viewer, so
    // without this line an unclaimed-looking card would offer a Claim button
    // that the server can only refuse.
    const groupBadge = prGroupBadgeText(task) ? (
      <span className="claimed-pill" style={{ background: '#eef2ff', color: '#3730a3', borderColor: '#c7d2fe' }}>
        {prGroupBadgeText(task)}
      </span>
    ) : null;
    const groupHolder = prGroupHeldByOther(task, user?.id);

    // ON-BEHALF SUBMISSION APPROVAL. Never a Claim/Release/Complete/Dismiss
    // card — the account's handler was assigned at insert time, and the only
    // action that exists is their decision. Everyone else (including admin
    // oversight) gets a read-only "who's deciding" line, no buttons: the
    // server would refuse anything they clicked anyway.
    if (task.task_type === APPROVAL_TASK_TYPE) {
      const isHandler = task.claimed_by === user?.id;
      return (
        <div className="action-buttons">
          {isHandler ? (
            <>
              <span className="claimed-pill claimed-pill--self">Your approval</span>
              <button
                onClick={() => handleApproveSubmission(task)}
                className="btn btn-small btn-success"
                disabled={decidingTaskId === task.id}
              >
                {decidingTaskId === task.id ? 'Approving…' : 'Approve'}
              </button>
              <button
                onClick={() => handleRejectSubmission(task)}
                className="btn btn-small btn-danger"
                disabled={decidingTaskId === task.id}
              >
                {decidingTaskId === task.id ? 'Rejecting…' : 'Reject'}
              </button>
            </>
          ) : (
            <span className="claimed-pill">
              {`Awaiting ${task.claimed_by_username || 'the handler'}'s approval`}
            </span>
          )}
        </div>
      );
    }

    if ((task.task_type === 'payment_request' || task.task_type === 'platform_order') && user?.role === 'finance') {
      // ONE code path for BOTH flows so they can never visually diverge — only
      // the data source differs (payee text + copy/claim/release/prepare handlers).
      // Admins fall through to the claimed-pill (exactly as they already do for a
      // payment_request): claiming is finance-only and the pool has no oversight
      // actions — platform-order oversight lives on the Platform page.
      const isPlatform = task.task_type === 'platform_order';
      const onClaim = () => (isPlatform ? handleClaimPlatformTask(task) : handleClaimTask(task));
      const onRelease = () => (isPlatform ? handleReleasePlatformTask(task) : handleReleaseTask(task));
      // A platform task HANDS OVER to the module that owns the order — say so on
      // the button, so the click isn't mistaken for "the form opens here".
      const onPrepare = () => (isPlatform ? handleAddPlatformPayment(task) : handleUploadReceipt(task));
      const prepareLabel = isPlatform ? 'Open in Platform Management' : prepareButtonLabel(task);
      const onCopy = () => (isPlatform ? handleCopyPlatformPayeeDetails(task) : handleCopyBankingDetails(task));
      const hasPayeeToCopy = isPlatform
        ? !!platformPayeeText(task)
        : (task.bank_name || task.account_name || task.account_number);
      return (
        <div className="action-buttons">
          {claimedPill}
          {groupBadge}
          {!task.claimed_by && groupHolder && (
            <span className="claimed-pill" title="Every payment of this set is claimed together — the current holder must release it first.">
              {`Part of ${prGroupScopeLabel(task)} is claimed by ${groupHolder}`}
            </span>
          )}
          {!task.claimed_by && !groupHolder && (
            <button
              onClick={onClaim}
              className="btn btn-small btn-warning"
              disabled={claimingTaskId === task.id}
              title={isPlatform ? undefined : (prIsGrouped(task)
                ? 'Claims only this payment — the other payments of the request can be claimed by anyone'
                : undefined)}
            >
              {claimingTaskId === task.id ? 'Claiming…' : (isPlatform ? 'Claim task' : prGroupClaimLabel(task))}
            </button>
          )}
          {task.claimed_by === user?.id && (() => {
            // Release keys on UNCONFIRMED money only (group-aware for payment
            // requests): completed tranches never pin a hand-off.
            const releaseBlocked = releaseBlockedByPrepared(task);
            // Fully prepared = the whole amount is already allocated to
            // prepared/completed tranches, so there's NOTHING left to pay. Hide the
            // payee details + pay affordance so finance can't accidentally pay it
            // AGAIN — it just needs completing (in Task Tracking) once the bank debits.
            // Shared gate (utils/paymentTask) so Task Tracking's claimed card can
            // never offer a prepare button this pool card wouldn't (and vice-versa).
            // The pill reads the ONE stage model (taskSteps): where the task
            // is and what happens next — a PAID order says "Order paid —
            // handler releases the crypto", never "mark complete" again.
            if (taskFullyPrepared(task)) {
              return <TaskProgressBar task={task} compact showHint style={{ alignSelf: 'center' }} />;
            }
            return (
              <>
                {hasPayeeToCopy && (
                  <button onClick={onCopy} className="btn btn-small btn-outline">
                    {copiedTaskId === task.id ? 'Copied' : 'Copy details'}
                  </button>
                )}
                <button onClick={onPrepare} className="btn btn-small btn-primary">
                  {prepareLabel}
                </button>
                {releaseBlocked && (
                  <span className="claimed-pill" title="Cancel the prepared slip, or mark it complete once the bank has debited, before releasing.">
                    Release blocked by an unconfirmed prepared slip
                  </span>
                )}
                {!releaseBlocked && (
                  <button onClick={onRelease} className="btn btn-small btn-outline"
                    title={releaseIsPerPayment(task)
                      ? 'Drop only this payment back to the pool — the other payments are not affected; completed slips stay, the next claimer continues the balance'
                      : 'Drop this task back to the pool for another finance user — completed payments stay, they continue the balance'}>
                    {releaseIsPerPayment(task) ? 'Release this payment' : 'Release'}
                  </button>
                )}
              </>
            );
          })()}
        </div>
      );
    }

    if (task.task_type === 'payment_received' && user?.role === 'finance') {
      // Sales-submitted "customer paid us" receipt: finance claims it, checks
      // the receipt, and records the credit through the shared recorder.
      return (
        <div className="action-buttons">
          {claimedPill}
          {!task.claimed_by && (
            <button onClick={() => handleClaimTask(task)} className="btn btn-small btn-warning"
              disabled={claimingTaskId === task.id}>
              {claimingTaskId === task.id ? 'Claiming…' : 'Claim task'}
            </button>
          )}
          {task.claimed_by === user?.id && (
            <>
              <button onClick={() => setRecordPrTask(task)} className="btn btn-small btn-primary">
                {prRecordButtonLabel(task)}
              </button>
              <button onClick={() => handleReleaseTask(task)} className="btn btn-small btn-outline"
                title="Drop this receipt back to the pool for another finance user">
                Release
              </button>
            </>
          )}
        </div>
      );
    }

    if (task.task_type === 'pr_notification') {
      // NOTICES (operator 2026-10-12): claimable by sales AND finance; whoever
      // holds it is the only one who may act on it (the server refuses the
      // rest), and acting on an unclaimed one claims it. ONE renderer with Task
      // Tracking's cards (NoticeTaskActions) so both offer the same buttons.
      return (
        <NoticeTaskActions
          task={task}
          user={user}
          actions={{ claimingNoticeId, handleClaimNotice, handleCreateOrderFromNotice, handleCreatePlatformOrderFromNotice, handleAssignToCustomer, handleTxAction }}
          onRelease={handleReleaseTask}
        />
      );
    }

    // EXPENSE APPROVAL (one per claim, target_role administrator, pool-claimable
    // by any admin — born unclaimed). The decision itself (approve/reject, a
    // reason required for rejection) lives on the Expenses & Profit page, never
    // here — this card only claims/releases and hands off.
    if (task.task_type === 'expense_approval') {
      if (user?.role !== 'administrator') {
        return (
          <div className="action-buttons">
            <span className="claimed-pill">Awaiting admin approval</span>
          </div>
        );
      }
      if (!task.claimed_by) {
        return (
          <div className="action-buttons">
            <button
              onClick={() => handleClaimTask(task)}
              className="btn btn-small btn-warning"
              disabled={claimingTaskId === task.id}
            >
              {claimingTaskId === task.id ? 'Claiming…' : 'Claim'}
            </button>
          </div>
        );
      }
      if (task.claimed_by === user?.id) {
        return (
          <div className="action-buttons">
            <span className="claimed-pill claimed-pill--self">Claimed by you</span>
            <span style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>
              Decide (approve or reject) on the Expenses & Profit page.
            </span>
            <button
              onClick={() => { navigate('/expenses', { state: { focusExpenseId: task.expense_id } }); onClose(); }}
              className="btn btn-small btn-primary"
            >
              Open in Expenses
            </button>
            <button
              onClick={() => handleReleaseTask(task)}
              className="btn btn-small btn-outline"
              title="Drop this claim back to the pool for another admin"
            >
              Release
            </button>
          </div>
        );
      }
      return (
        <div className="action-buttons">
          <span className="claimed-pill">{`Held by ${task.claimed_by_username || 'another admin'}`}</span>
        </div>
      );
    }

    // EXPENSE PAYMENT (one per payee of an APPROVED claim, target_role finance,
    // pool-claimable). Finance pays THIS payee via the Expenses & Profit page's
    // prepare/complete surfaces — never here.
    if (task.task_type === 'expense_payment') {
      if (user?.role !== 'finance') {
        return claimedPill ? <div className="action-buttons">{claimedPill}</div> : null;
      }
      // THE expense-payee gate (utils/paymentTask expensePayeeGate) — the same
      // Claim / Release decision Task Tracking, the Expenses page and the
      // server make for this payee.
      const gate = expensePayeeGate(expenseGateInputFromTask(task), user);
      if (gate.canClaim) {
        return (
          <div className="action-buttons">
            <button
              onClick={() => handleClaimTask(task)}
              className="btn btn-small btn-warning"
              disabled={claimingTaskId === task.id}
            >
              {claimingTaskId === task.id ? 'Claiming…' : 'Claim'}
            </button>
          </div>
        );
      }
      if (gate.mine) {
        return (
          <div className="action-buttons">
            <span className="claimed-pill claimed-pill--self">Claimed by you</span>
            <button
              onClick={() => { navigate('/expenses', { state: { focusExpenseId: task.expense_id } }); onClose(); }}
              className="btn btn-small btn-primary"
            >
              Pay in Expenses
            </button>
            {/* Only an UNCONFIRMED (prepared) slip pins the release; completed
                slips stay and the next claimer pays the remainder. */}
            {gate.releaseBlocked && (
              <span className="claimed-pill" title={EXPENSE_RELEASE_BLOCKED_TEXT}>
                Release blocked by an unconfirmed prepared slip
              </span>
            )}
            {gate.canRelease && (
              <button
                onClick={() => handleReleaseTask(task)}
                className="btn btn-small btn-outline"
                title="Drop this payment back to the pool for another finance user — completed slips stay, they pay the remainder"
              >
                Release
              </button>
            )}
          </div>
        );
      }
      return gate.held ? (
        <div className="action-buttons">
          <span className="claimed-pill">{`Claimed by ${task.claimed_by_username}`}</span>
        </div>
      ) : null;
    }

    return claimedPill ? <div className="action-buttons">{claimedPill}</div> : null;
  };

  return (
    <>
      {pendingTasks.length > 0 && (
        <div className="pool-cats" role="group" aria-label="Filter tasks by category">
          <button
            type="button"
            className={`pool-cat${activeCategory === POOL_CATEGORY_ALL ? ' active' : ''}`}
            aria-pressed={activeCategory === POOL_CATEGORY_ALL}
            onClick={() => pickCategory(POOL_CATEGORY_ALL)}
          >
            All <span className="pool-cat-count">{pendingTasks.length}</span>
          </button>
          {POOL_CATEGORIES
            // A category with nothing in it is not offered — except the one the
            // user is sitting on, so the strip still shows where they are.
            .filter((c) => categoryCounts[c.key] > 0 || c.key === activeCategory)
            .map((c) => (
              <button
                key={c.key}
                type="button"
                className={`pool-cat${activeCategory === c.key ? ' active' : ''}`}
                aria-pressed={activeCategory === c.key}
                onClick={() => pickCategory(c.key)}
              >
                {c.label} <span className="pool-cat-count">{categoryCounts[c.key] || 0}</span>
              </button>
            ))}
          <span className="pool-cat-scope">
            {emptyChoiceLabel ? `${emptyChoiceLabel} has nothing on this list, so All is shown. ` : ''}
            {unclaimedOnly ? 'Counts cover unclaimed tasks only.' : 'Counts include tasks that are already claimed.'}
          </span>
        </div>
      )}
      {pendingTasks.length === 0 ? (
        <div className="no-tasks-message">
          <p>Nothing to do right now.</p>
        </div>
      ) : visibleTasks.length === 0 ? (
        <div className="no-tasks-message">
          <p>
            {POOL_CATEGORIES.find((c) => c.key === activeCategory)?.empty || 'Nothing to do right now.'}{' '}
            <button type="button" className="pool-cat-showall" onClick={() => pickCategory(POOL_CATEGORY_ALL)}>
              Show all
            </button>
          </p>
        </div>
      ) : (
        <div className="tasks-list">
          {visibleTasks.map(task => {
            const lines = [];
            // The receipt SET behind a payment_received task (Section 7 of the
            // multi-receipt spec) — computed ONCE per task so the progress line
            // below and the gallery further down can never read a different
            // count from the same array (prReceipts is the ONE parser).
            const prReceiptRows = task.task_type === 'payment_received' ? prReceipts(task) : [];
            // Finance's receipts on an expense payee = its slip SET (expenseSlips
            // is the ONE parser, shared with Task Tracking).
            const expenseSlipRows = expenseSlips(task);
            // WHAT THE SOURCE ROW ACTUALLY SAYS wins once it exists — read live
            // off the referenced transaction, so a third-party payer name (or
            // any later correction to it) shows instead of the name sales
            // guessed at submission / the regex scraped at insert time. On a
            // payment_received the sales note stays, DEMOTED, because it is an
            // independent fact; on a pr_notification the stored description is
            // the same transaction text frozen, so it is dropped rather than
            // shown as a second, contradictory answer.
            const recordedName = prRecordedName(task);
            if (recordedName) lines.push({ label: prRecordedLabel(task), value: (<>{renderTextWithAddressLabels(recordedName)}{task.task_type === 'payment_received' && <CopyTextButton text={prTaskCopyLine(task)} label="Copy PR line" />}</>) });
            // SPLIT receipts (operator 2026-09-24): no task-level name exists on a
            // 2+-slip task — list the payer finance recorded on EACH slip with
            // its amount (see prRecordedReceiptLines).
            const perSlip = prRecordedReceiptLines(task);
            if (perSlip.length > 0) {
              lines.push({
                label: `Recorded by finance (${prReceiptProgress(task).recorded} of ${prReceiptProgress(task).total})`,
                value: (
                  <div>
                    {perSlip.map((ln) => (
                      <div key={ln.index}>
                        #{ln.index} — {renderTextWithAddressLabels(ln.text)}
                        {ln.sub ? <span style={{ color: '#6b7280' }}> · {ln.sub}</span> : null}
                        <CopyTextButton text={ln.copy} label="Copy PR line" />
                      </div>
                    ))}
                  </div>
                ),
              });
            }
            // Descriptions bake in raw addresses (e.g. "…- from T…"); render any
            // address as its saved label with the full address on hover.
            if (task.description && !(recordedName && !prHasSalesNote(task))) {
              lines.push({
                label: recordedName ? 'Sales note' : 'Description',
                value: renderTextWithAddressLabels(task.description),
              });
            }
            // The sales person's optional reference on a payment request
            // (operator 2026-10-10), straight from the task row.
            if (task.task_type === 'payment_request' && task.sales_remark) {
              lines.push({ label: 'Sales remark', value: task.sales_remark });
            }
            // Multi-receipt progress ("RM x of RM y recorded (n of m)") — only
            // once there is more than one slip to report on; a single-receipt
            // task has nothing a plain PENDING/RECORDED chip doesn't already say.
            if (prReceiptRows.length > 1) {
              lines.push({ label: 'Progress', value: prProgressText(task) });
            }
            if (task.order_id) lines.push({ label: 'Order', value: `#${task.order_id}` });
            // Platform-order tasks link via platform_order_id (not order_id); surface
            // the order number the same way so finance can cross-reference it.
            // The on-behalf approval task resolves BY REFERENCE off the same
            // order (isPlatformShaped), so it gets the same line rather than
            // a second, duplicated condition.
            if (isPlatformShaped(task) && task.platform_order_id) {
              lines.push({ label: 'Order', value: `Platform #${task.platform_order_id}` });
              // WHICH platform account + wallet (operator 2026-10-09: "it should
              // also show what platform is it") — the same facts as the Platform
              // Orders list's PLATFORM / WALLET columns, read off the order.
              const platformLabel = formatPlatformAccountLabel(task);
              if (platformLabel) lines.push({ label: 'Platform', value: platformLabel });
            }
            if (task.customer_name) lines.push({ label: 'Customer', value: task.customer_name });
            const accountLines = getAccountDetailsLines(task);
            // Once a payment_request is fully prepared (nothing left to pay), hide
            // the payee bank line too so finance can't read off the account and pay again.
            // WHO may read it is the server's rule (maskTaskListForViewer —
            // it nulls the line for everyone else and sets
            // payee_details_locked); taskPayeeAccess is its client mirror, so
            // this check is redundant to the server, never wider or narrower.
            const prFullyPrepared = task.task_type === 'payment_request' && taskFullyPrepared(task);
            const showBank = (task.bank_name || task.transaction_bank_name) &&
              (task.task_type !== 'payment_request' ||
                (!task.payee_details_locked && taskPayeeAccess(task, user) !== 'none' && !prFullyPrepared));
            if (showBank && accountLines.length > 0) {
              lines.push({ label: 'Bank', value: accountLines.join(' · '), mono: true });
            }
            // Which of OUR accounts this task's transaction sits on.
            if (task.source_account_name) {
              const typeLabel = task.source_account_type === 'Blockchain Wallet' && task.source_account_chain
                ? `Blockchain Wallet (${task.source_account_chain})`
                : task.source_account_type;
              lines.push({ label: 'Source', value: `${typeLabel || 'Account'} - ${task.source_account_name}` });
            }
            // On-chain TX hash (for a blockchain-transaction task) → clickable
            // block-explorer link.
            if (task.blockchain_tx_hash) {
              lines.push({ label: 'TX', value: <TxHashLink hash={task.blockchain_tx_hash} chain={task.source_account_chain} truncate={10} />, mono: true });
            }
            if (task.created_by_username && user?.role !== 'sales') {
              lines.push({ label: 'Created by', value: task.created_by_username });
            }
            // The approval task's handler IS sales — the rule above (hide
            // "Created by" from sales) exists to keep other sales reps from
            // seeing who submitted an ordinary task, and would hide the one
            // fact the handler needs here. Push it explicitly rather than
            // loosening that rule for everyone.
            if (task.task_type === APPROVAL_TASK_TYPE && task.created_by_username) {
              lines.push({ label: 'Requested by', value: task.created_by_username });
            }
            // EXPENSE CLAIM/PAYMENT context — resolved BY REFERENCE off the live
            // expenses row on every task query, so a later edit to the claim
            // (title, category) never leaves this card showing a stale copy.
            if (task.task_type === 'expense_approval' || task.task_type === 'expense_payment') {
              if (task.expense_title) {
                lines.push({ label: 'Claim', value: `${task.expense_title}${task.expense_kind ? ` (${task.expense_kind})` : ''}` });
              }
              if (task.task_type === 'expense_payment' && Number(task.expense_detail_total) > 1) {
                lines.push({ label: 'Payment', value: `${task.expense_detail_seq} of ${task.expense_detail_total}` });
              }
              if (Number(task.expense_detail_total) > 1) {
                lines.push({ label: 'Progress', value: `${task.expense_detail_completed}/${task.expense_detail_total} paid` });
              }
              // The requester is already on the card as "Created by" (the
              // generic line above, which finance and admin both see, and these
              // tasks are theirs) — a second "Requested by" with the same name
              // was pure duplication. Sales never sees this card at all.
            }
            lines.push({ label: 'Date', value: formatDate(task.created_at) });

            const balancePill = customerBalancePill(task);

            return (
              // id is what notification routing scrolls to: arriving from a
              // "new task" alert lands on the pool, and without a handle on the
              // row there is nothing to scroll the operator to.
              <div key={task.id} id={`task-${task.id}`} className="task-item"
                   style={focusTaskId === task.id ? { boxShadow: '0 0 0 2px #3b82f6', borderRadius: 8 } : undefined}>
                <div className="task-top-row">
                  <div className="task-top-left">
                    <span className="task-title">{displayTaskTitle(task)}</span>
                    {/* FRONT/BACK ADS on the task itself (operator 2026-10-09) — read
                        from the order (task.platform_ads_type), never the task text. */}
                    <AdsTypeChip adsType={task.platform_ads_type} style={{ marginLeft: 6 }} />
                    {balancePill}
                    {/* The jump to Task Tracking sits in the TOP ROW so EVERY card
                        type carries it (money tasks, platform tasks, crypto/bank
                        notices, approvals) — the action rows below differ per type. */}
                    {trackingLink && (
                      <button
                        type="button"
                        className="task-tracking-jump"
                        title="Go to this task in Task Tracking (it opens in All Tasks, expanded and highlighted)"
                        onClick={(e) => { e.stopPropagation(); onClose(); navigate('/task-tracking', { state: { focusTaskId: task.id } }); }}
                      >
                        Open in Task Tracking ↗
                      </button>
                    )}
                  </div>
                  <span className="task-amount">
                    {isPartiallyAllocated(task)
                      ? `${formatTaskMoney(remainingToPay(task), task)} left of ${getTaskAmountDisplay(task)}`
                      : getTaskAmountDisplay(task)}
                  </span>
                </div>

                <dl className="task-details">
                  {lines.map((ln, i) => (
                    <div key={i} className="task-detail-row">
                      <dt className="detail-label">{ln.label}</dt>
                      <dd className={`detail-value${ln.mono ? ' detail-value--mono' : ''}`}>{ln.value}</dd>
                    </div>
                  ))}
                </dl>

                {/* Receipt GALLERY over the slip SET (prReceipts) — replaces the
                    old single task.request_proof_url image. Each slip carries
                    its own RECORDED ✓ / PENDING chip so finance can tell at a
                    glance which of several receipts still need recording,
                    without opening the record modal first. Mirrors the sell-
                    receipt gallery in TaskTracking (~793) so the two flows
                    read the same. */}
                {prReceiptRows.length > 0 && (
                  <div className="banking-details-section">
                    <div className="banking-details-header">
                      <span className="banking-details-label">
                        {prReceiptRows.length > 1 ? `Customer's payment receipts (${prReceiptRows.length})` : "Customer's payment receipt"}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                      {prReceiptRows.map((r, ri) => {
                        const recorded = r.bank_transaction_id != null;
                        return (
                          <div key={r.id ?? `legacy-${ri}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <a href={resolveUploadUrl(r.url)} target="_blank" rel="noopener noreferrer" title="Click for full size">
                              <img
                                src={resolveUploadUrl(r.url)}
                                alt={prReceiptRows.length > 1 ? `Customer's payment receipt ${ri + 1} of ${prReceiptRows.length}` : "Customer's payment receipt"}
                                style={{ maxWidth: 'min(320px, 100%)', maxHeight: 260, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'block' }}
                              />
                            </a>
                            <div style={{ fontSize: 12 }}>
                              {prReceiptRows.length > 1 ? `#${ri + 1} of ${prReceiptRows.length} — ` : ''}
                              stated {formatCurrency(r.stated_amount)}
                              <span
                                style={{
                                  marginLeft: 6, fontSize: 11, fontWeight: 700, borderRadius: 4, padding: '1px 6px',
                                  ...(recorded
                                    ? { color: '#166534', background: '#f0fdf4', border: '1px solid #86efac' }
                                    : { color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d' }),
                                }}
                              >
                                {recorded ? 'RECORDED ✓' : 'PENDING'}
                              </span>
                              {prReceiptRecordText(r) && (
                                <div style={{ marginTop: 3, color: '#374151' }}>
                                  Recorded by finance: {renderTextWithAddressLabels(prReceiptRecordText(r))}
                                  {prReceiptRecordSub(r) ? <span style={{ color: '#6b7280' }}> · {prReceiptRecordSub(r)}</span> : null}
                                  <CopyTextButton text={prReceiptCopyLine(r)} label="Copy PR line" />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Copyable banking details + the customer's proof screenshot,
                    shown once YOU have claimed the task (and hidden once it's
                    fully prepared so finance can't re-copy the payee and pay
                    again). ONE panel for payment_request, platform_order AND
                    the on-behalf approval task — the approval task resolves
                    the same payee/screenshot BY REFERENCE off the order it
                    names, so the handler can see what they're approving.
                    isPlatformShaped keeps the two platform-shaped types from
                    drifting; only the payee data source differs from
                    payment_request (bank_name/account_name vs
                    platform_payee_json). */}
                {(task.task_type === 'payment_request' || isPlatformShaped(task) || task.task_type === 'expense_payment') &&
                  task.claimed_by === user?.id &&
                  !taskFullyPrepared(task) &&
                  (isPlatformShaped(task)
                    ? !!platformPayeeText(task)
                    : task.task_type === 'expense_payment'
                      ? !!(task.expense_payee_details || task.expense_payee_bank_name || task.expense_payee_account_name || task.expense_payee_account_number)
                      : (task.bank_name || task.account_name || task.account_number)) && (
                  <div className="banking-details-section">
                    <div className="banking-details-header">
                      <span className="banking-details-label">Banking details</span>
                      <button
                        className="btn btn-small btn-outline"
                        onClick={() => (isPlatformShaped(task) ? handleCopyPlatformPayeeDetails(task) : handleCopyBankingDetails(task))}
                        title="Click to copy banking details"
                      >
                        {copiedTaskId === task.id ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <pre
                        className="banking-details-content"
                        onClick={() => (isPlatformShaped(task) ? handleCopyPlatformPayeeDetails(task) : handleCopyBankingDetails(task))}
                        style={{ flex: '1 1 220px' }}
                      >
                        {isPlatformShaped(task) ? platformBankingDetailsText(task) : bankingDetailsText(task)}
                      </pre>
                      {/* PLATFORM BUY (operator 2026-10-10): the proof slot shows the
                          per-payee "Payment proof (details shared at platform)" — this
                          card's payee on a split buy — instead of the trade screenshot
                          that used to sit here as "Customer's payee proof". */}
                      {isPlatformShaped(task) && platformPayeeProofs(task).length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: '0 1 auto' }}>
                          {platformPayeeProofs(task).map((pp) => (
                            <a key={`payee-proof-${pp.seq}`} href={resolveUploadUrl(pp.url)} target="_blank" rel="noopener noreferrer" title={`Payment proof (details shared at platform)${task.platform_order_detail_seq != null || platformPayeeProofs(task).length > 1 ? ` · payee #${pp.seq}` : ''} — click for full size`}>
                              <img
                                src={resolveUploadUrl(pp.url)}
                                alt={`Payment proof for payee ${pp.seq}`}
                                style={{ maxWidth: 'min(480px, 100%)', maxHeight: 420, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'block' }}
                              />
                            </a>
                          ))}
                        </div>
                      ) : task.request_proof_url && (
                        <a href={resolveUploadUrl(task.request_proof_url)} target="_blank" rel="noopener noreferrer" title={isPlatformShaped(task) ? 'Platform trade screenshot — click for full size' : "Customer's proof — click for full size"} style={{ flex: '0 1 auto' }}>
                          <img
                            src={resolveUploadUrl(task.request_proof_url)}
                            alt={isPlatformShaped(task) ? 'Platform trade screenshot' : "Customer's payee proof"}
                            style={{ maxWidth: 'min(480px, 100%)', maxHeight: 420, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'block' }}
                          />
                        </a>
                      )}
                      {/* Expense claim: the submitter's compulsory proof, and —
                          once finance has prepared this payee — their own
                          receipt. Same treatment as request_proof_url above,
                          just a different source column. */}
                      {task.task_type === 'expense_payment' && task.expense_invoice_url && (
                        /* The claim's receipt SET (expense_invoice_urls, newline-joined; one
                           per receipt paid together) — falls back to the single url. */
                        (String(task.expense_invoice_urls || task.expense_invoice_url).split('\n').filter(Boolean)).map((u, i, arr) => (
                          <a key={`${u}-${i}`} href={resolveUploadUrl(u)} target="_blank" rel="noopener noreferrer" title={arr.length > 1 ? `Submitter's receipt ${i + 1} of ${arr.length} — click for full size` : "Submitter's proof — click for full size"} style={{ flex: '0 1 auto' }}>
                            <img
                              src={resolveUploadUrl(u)}
                              alt={arr.length > 1 ? `Submitter's receipt ${i + 1}` : "Submitter's proof"}
                              style={{ maxWidth: arr.length > 1 ? 'min(240px, 100%)' : 'min(480px, 100%)', maxHeight: 420, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'block' }}
                            />
                          </a>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Finance's receipts of an expense payee: every slip, laid out like
                    the payment-request tranche gallery in Task Tracking (label,
                    status chip, who prepared/paid it and when, Download). Its own
                    block — not nested in the banking panel above — so a PREPARED
                    slip stays visible whatever that panel's claim/fully-prepared
                    gates say. */}
                {expenseSlipRows.length > 0 && (
                  <div className="banking-details-section">
                    <div className="banking-details-header">
                      <span className="banking-details-label">
                        {expenseSlipRows.length > 1 ? `Finance's receipts (${expenseSlipRows.length})` : "Finance's receipt"}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                      {expenseSlipRows.map((s, si) => {
                        const chip = expenseSlipChip(s);
                        const who = expenseSlipWho(s);
                        const source = expenseSlipSourceText(s);
                        return (
                          <div key={s.id ?? `exp-slip-${si}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {s.url && (
                              <a href={resolveUploadUrl(s.url)} target="_blank" rel="noopener noreferrer" title="Finance's receipt — click for full size">
                                <img
                                  src={resolveUploadUrl(s.url)}
                                  alt={`Finance's receipt ${si + 1}`}
                                  style={{ maxWidth: 'min(320px, 100%)', maxHeight: 260, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'block' }}
                                />
                              </a>
                            )}
                            <div style={{ fontSize: 12 }}>
                              {expenseSlipLabel(s, si, expenseSlipRows.length)}
                              {source ? ` (${source})` : ''}
                              <span
                                style={{
                                  marginLeft: 6, fontSize: 11, fontWeight: 700, borderRadius: 4, padding: '1px 6px',
                                  ...(chip.prepared
                                    ? { color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d' }
                                    : { color: '#166534', background: '#f0fdf4', border: '1px solid #86efac' }),
                                }}
                              >
                                {chip.text}
                              </span>
                              {who && (
                                <div style={{ marginTop: 3, color: '#374151' }}>
                                  <strong>{who.verb}:</strong> {who.name}
                                  {who.at ? ` on ${formatDate(who.at)}` : ''}
                                  {who.preparedBy && <div><strong>Prepared by:</strong> {who.preparedBy}</div>}
                                </div>
                              )}
                              {s.url && (
                                <a href={resolveUploadUrl(s.url)} download target="_blank" rel="noopener noreferrer" className="btn btn-small btn-outline" style={{ marginTop: 6, display: 'inline-block' }}>Download</a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {renderActions(task)}
              </div>
            );
          })}
        </div>
      )}

      {recordPrTask && (
        <RecordPaymentReceivedModal
          task={recordPrTask}
          onClose={() => setRecordPrTask(null)}
          onSuccess={() => { onTaskUpdate && onTaskUpdate(); }}
          // Partial record (2+ receipts, more still pending): the modal stays
          // open on the SAME `recordPrTask` object and keeps recording off its
          // own local receipt state (Phase-6 — see the modal's header note),
          // so refreshing the pool here is safe and never tears the modal down.
          onPartial={onTaskUpdate}
        />
      )}
      <FulfillPaymentRequestModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onSuccess={handleReceiptUploaded}
        onPartial={onTaskUpdate}
      />

      {/* NOTE: the platform_order fulfil modal is NOT hosted here any more. It
          lives on Platform Management, which owns the order, and this pool
          navigates there — see handleAddPlatformPayment. */}
    </>
  );
}

export default PendingTaskList;
